/**
 * Debug-only bone/socket visualization: draws a character's joint hierarchy
 * as colored line segments (one per parent-bone -> child-bone pair) plus a
 * small colored sphere at each non-skinned attachment socket (e.g.
 * `Palm.L`/`Palm.R` - see `palm-sockets.mts`'s `addPalmSockets`).
 * Exists because eyeballing a *skinned* render to judge whether a socket
 * sits on the correct side of a hand has repeatedly proven unreliable (a
 * closed fist looks plausible from most angles regardless of which side a
 * rigid attachment is actually on) - this renders the raw joint positions
 * directly instead, removing that ambiguity.
 *
 * Deliberately independent of `@sorrem/shared`'s `AttachmentSlot`/
 * `SkeletonType` - it only needs `LoadedCharacterMesh.transformNodes` (every
 * TransformNode the glTF import produced, joints included - see that
 * field's own doc comment on why bone poses must be read from these nodes,
 * not `Skeleton.bones`) and plain name-pattern matching, so it works for any
 * humanoid-shaped rig without needing the catalog data loaded.
 */
import {
    Color3,
    Color4,
    MeshBuilder,
    StandardMaterial,
    TransformNode,
    type LinesMesh,
    type Mesh,
    type Scene,
} from '@babylonjs/core';
import type { LoadedCharacterMesh } from './character/character-mesh';
import { clamp01 } from './vfx/easing';

export interface SkeletonVisualization {
    /** Re-reads every joint/socket's current (animated) world position and updates the drawn lines/markers accordingly. Call once per rendered frame while the visualization is shown. */
    updatePose(): void;
    dispose(): void;
}

type BoneCategory = 'core' | 'arm' | 'finger' | 'leg' | 'socket' | 'other';

/** Base hue per category - side shading (see `shadeForSide`) is applied on top of these. */
const CATEGORY_BASE_COLOR: Record<BoneCategory, Color3> = {
    core: new Color3(0.95, 0.8, 0.25), // gold - spine/head/neck, the un-sided trunk of the rig
    arm: new Color3(0.25, 0.5, 0.95), // blue - shoulder/arm/elbow/hand
    finger: new Color3(0.3, 0.85, 0.45), // green - thumb/index/middle/ring/pinky segments
    leg: new Color3(0.95, 0.45, 0.2), // orange - hip/knee/foot/toe
    socket: new Color3(0.95, 0.15, 0.85), // magenta - non-skinned attachment points (Palm.L/R, ...)
    other: new Color3(0.55, 0.55, 0.55), // gray fallback - unrecognized names (e.g. the Armature root wrapper)
};

const CORE_BONE_NAMES = new Set(['Root', 'Pelvis', 'Spine.01', 'Spine.02', 'Chest', 'Neck', 'Head', 'Eyebrows', 'Eyes']);
const SOCKET_NAME_PATTERN = /^Palm\.[LR]$/;
const GRIP_AXIS_NAME_PATTERN = /^GripAxis\.[LR]$/;
const GRIP_AXIS_END_NAME_PATTERN = /^GripAxisEnd\.[LR]$/;
const FINGER_NAME_PATTERN = /^(Thumb|Index|Middle|Ring|Pinky)\.\d+\.([LR])$/;
const ARM_NAME_PATTERN = /^(Shoulder|Arm|Elbow|Hand)\.([LR])$/;
const LEG_NAME_PATTERN = /^(Hip|Knee|Foot|Toe(\.Tip)?)\.([LR])$/;

function resolveBoneCategory(name: string): { category: BoneCategory; side: 'L' | 'R' | null } {
    if (SOCKET_NAME_PATTERN.test(name)) {
        return { category: 'socket', side: name.endsWith('.L') ? 'L' : 'R' };
    }
    if (GRIP_AXIS_NAME_PATTERN.test(name)) {
        return { category: 'arm', side: name.endsWith('.L') ? 'L' : 'R' };
    }
    const finger = FINGER_NAME_PATTERN.exec(name);
    if (finger) {
        return { category: 'finger', side: finger[2] === 'L' ? 'L' : 'R' };
    }
    const arm = ARM_NAME_PATTERN.exec(name);
    if (arm) {
        return { category: 'arm', side: arm[2] === 'L' ? 'L' : 'R' };
    }
    const leg = LEG_NAME_PATTERN.exec(name);
    if (leg) {
        return { category: 'leg', side: leg[3] === 'L' ? 'L' : 'R' };
    }
    if (CORE_BONE_NAMES.has(name)) {
        return { category: 'core', side: null };
    }
    return { category: 'other', side: null };
}

/** Left is a lighter tint of the category hue, right a darker shade - un-sided (core) bones are left at the base color. */
function shadeForSide(base: Color3, side: 'L' | 'R' | null): Color3 {
    if (side === 'L') {
        return new Color3(clamp01(base.r * 1.3 + 0.15), clamp01(base.g * 1.3 + 0.15), clamp01(base.b * 1.3 + 0.15));
    }
    if (side === 'R') {
        return new Color3(base.r * 0.6, base.g * 0.6, base.b * 0.6);
    }
    return base;
}

function resolveBoneColor(name: string): Color3 {
    const { category, side } = resolveBoneCategory(name);
    return shadeForSide(CATEGORY_BASE_COLOR[category], side);
}

const SOCKET_MARKER_DIAMETER = 0.02;

export function createSkeletonVisualization(scene: Scene, character: LoadedCharacterMesh): SkeletonVisualization {
    const nodeSet = new Set<TransformNode>(character.transformNodes);

    // One (parent, child) pair per drawn segment - the child's name decides
    // the segment's color/category (e.g. the Hand.R -> Palm.R segment reads
    // as a distinct "socket" color, not "arm", which is exactly the point).
    // Every case touching GripAxis.{L,R}/GripAxisEnd.{L,R} is excluded from
    // this list entirely (no replacement rendering) rather than drawn
    // plainly - these are pure plumbing nodes with no anatomical meaning of
    // their own (the actual socket position now comes from live per-frame
    // `dynamicSocket` computation, see `dynamic-sockets.ts` -
    // `palm-sockets.mts`'s baked GripAxis/GripAxisEnd/Palm chain
    // is only a static fallback now), and drawing them as regular skeleton
    // lines made the hand's rig look like it had an extra bone laid across
    // it that isn't really there.
    const segments: { parent: TransformNode; child: TransformNode }[] = [];
    const socketNodes: TransformNode[] = [];
    for (const node of character.transformNodes) {
        const parent = node.parent;
        if (parent instanceof TransformNode && nodeSet.has(parent)) {
            if (!GRIP_AXIS_NAME_PATTERN.test(node.name) && !GRIP_AXIS_END_NAME_PATTERN.test(node.name) && !(GRIP_AXIS_NAME_PATTERN.test(parent.name) && SOCKET_NAME_PATTERN.test(node.name))) {
                segments.push({ parent, child: node });
            }
        }
        if (SOCKET_NAME_PATTERN.test(node.name)) {
            socketNodes.push(node);
        }
    }

    let linesMesh: LinesMesh | null = null;

    function buildLines(): void {
        if (segments.length === 0) return;
        const lines = segments.map((segment) => [segment.parent.getAbsolutePosition(), segment.child.getAbsolutePosition()]);
        const colors = segments.map((segment) => {
            const color = Color4.FromColor3(resolveBoneColor(segment.child.name), 1);
            return [color, color];
        });
        linesMesh = MeshBuilder.CreateLineSystem(
            'skeleton-viz-lines',
            { lines, colors, updatable: true, instance: linesMesh ?? undefined },
            scene,
        );
        linesMesh.renderingGroupId = 1; // draw over the (hidden) character mesh's group, consistent with other debug overlays
    }

    const socketMaterial = new StandardMaterial('skeleton-viz-socket-material', scene);
    socketMaterial.diffuseColor = CATEGORY_BASE_COLOR.socket;
    socketMaterial.emissiveColor = CATEGORY_BASE_COLOR.socket;
    socketMaterial.disableLighting = true;

    const socketMarkers: Mesh[] = socketNodes.map((socketNode) => {
        const marker = MeshBuilder.CreateSphere(`skeleton-viz-socket-${socketNode.name}`, { diameter: SOCKET_MARKER_DIAMETER }, scene);
        marker.material = socketMaterial;
        marker.parent = socketNode;
        marker.position.set(0, 0, 0);
        return marker;
    });

    buildLines();

    return {
        updatePose(): void {
            buildLines();
            // Socket markers are parented directly to their socket node, so
            // they track its animated world position for free - nothing to
            // update here.
        },
        dispose(): void {
            linesMesh?.dispose();
            linesMesh = null;
            for (const marker of socketMarkers) marker.dispose();
            socketMaterial.dispose();
        },
    };
}
